import type { FC } from 'react';
import ConfirmPopup from '~/components/shared/ConfirmPopup';

interface IRebookPathPopupProps {
  /** Professional being rebooked — shown in the heading when known. */
  professionalName?: string;
  /** Invite the professional to a shift the office has already posted. */
  onExistingShift: () => void;
  /** Post a new shift and invite the professional to it. */
  onNewShift: () => void;
  /** Dismiss without choosing a path. */
  onClose: () => void;
}

/**
 * Asks which rebook path to take, mirroring the two options `InviteButton` offers in its
 * dropdown ("Existing Shift" / "New Shift").
 *
 * The DS shift card owns its own `⋯` dropdown and its items are flat (`label` + `onSelect`,
 * and the menu closes on select), so the two paths cannot be a nested submenu there. Asking
 * in a popup keeps the card menu itself matching the design (Chat / Rebook / Add to Fav)
 * while still reaching both paths.
 */
export const RebookPathPopup: FC<IRebookPathPopupProps> = ({
  professionalName,
  onExistingShift,
  onNewShift,
  onClose,
}) => (
  <ConfirmPopup
    dataCy="rebook-path-popup"
    title={professionalName ? `Rebook ${professionalName}` : 'Rebook Professional'}
    description="Invite this professional to a shift you have already posted, or post a new shift for them."
    showClosePopupIcon
    closePopup={onClose}
    leftButtonText="Existing Shift"
    leftButtonAction={onExistingShift}
    leftButtonDataCy="rebook-existing-shift"
    rightButtonText="New Shift"
    rightButtonAction={onNewShift}
    rightButtonDataCy="rebook-new-shift"
  />
);
